import { useRef, useState } from "react";
import { ArrowDropDown, Download } from "@mui/icons-material";
import { Button, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from "@mui/material";

import { rowsToMultiColumnCsv, triggerCsvDownload } from "../../components/ExportCSV";

/**
 * @param {{
 *  disabled?: boolean,
 *  onError?: (error: unknown) => void,
 *  options: Array<{
 *    id: string,
 *    label: string,
 *    columns: { key: string, label: string }[],
 *    fileName: string,
 *    loadRows: () => Promise<Record<string, unknown>[]>,
 *  }>,
 * }} props
 */
function PricingReportCsvExport({ disabled = false, onError, options }) {
  const anchorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loadingOptionId, setLoadingOptionId] = useState(null);

  const isLoading = loadingOptionId != null;
  const exportDisabled = disabled || isLoading || !options?.length;

  const runExport = async (option) => {
    setLoadingOptionId(option.id);
    setMenuOpen(false);
    try {
      const rows = await option.loadRows();
      if (!rows?.length) {
        // eslint-disable-next-line no-console
        console.warn("No data available for CSV export");
        return;
      }
      const csvString = rowsToMultiColumnCsv(option.columns, rows);
      triggerCsvDownload(csvString, option.fileName);
    } catch (error) {
      onError?.(error);
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setLoadingOptionId(null);
    }
  };

  const handleMenuToggle = () => {
    if (exportDisabled) return;
    setMenuOpen((open) => !open);
  };

  const handleMenuClose = (event) => {
    if (anchorRef.current?.contains(event.target)) {
      return;
    }
    setMenuOpen(false);
  };

  return (
    <>
      <Button
        ref={anchorRef}
        variant="outlined"
        color="secondary"
        size="small"
        disabled={exportDisabled}
        onClick={handleMenuToggle}
        startIcon={<Download />}
        endIcon={<ArrowDropDown />}
        aria-haspopup="menu"
        aria-expanded={menuOpen ? "true" : undefined}
        aria-controls={menuOpen ? "pricing-report-csv-export-menu" : undefined}
      >
        {isLoading ? "Downloading…" : "Export to CSV"}
      </Button>
      <Popper
        open={menuOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper elevation={4}>
              <ClickAwayListener onClickAway={handleMenuClose}>
                <MenuList id="pricing-report-csv-export-menu" autoFocusItem={menuOpen}>
                  {(options || []).map((option) => (
                    <MenuItem
                      key={option.id}
                      disabled={isLoading}
                      onClick={() => void runExport(option)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

export default PricingReportCsvExport;
